import { CameraAltOutlined, ImageOutlined } from '@mui/icons-material'
import { CircularProgress } from '@mui/material'
import { useRef, useState } from 'react'
import AvatarEditor from 'react-avatar-editor'
import styles from './avatarUploader.module.css'
import CustomButton from '../../../components/Button'
import CustomSlider from '../../../components/Slider'

export function AvatarPlaceholder() {
    return (
        <div className="flex h-full w-full items-center justify-center bg-light rounded-full">
            <CameraAltOutlined
                className="transition-all duration-200 group-hover:text-gray"
                fontSize="large"
            />
        </div>
    )
}

interface FormProps {
    handleClose: () => void
    setAvatarImage: (image: string) => void
}

function AvatarUploader({ handleClose, setAvatarImage }: FormProps) {
    const [zoom, setZoom] = useState<number>(10)
    const [image, setImage] = useState<File | null>(null)
    const fileRef = useRef<HTMLInputElement>(null)
    const editorRef = useRef<AvatarEditor>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string>('')

    const saveHandler = async () => {
        if (editorRef.current && !!image) {
            setLoading(true)
            const originalImage = editorRef.current.getImage()

            setAvatarImage(
                originalImage.toDataURL('image/png').replace('image/png', 'image/octet-stream'),
            )

            setLoading(false)
            handleClose()
        } else {
            setLoading(true)
            setAvatarImage('')
            setLoading(false)
            handleClose()
        }
    }

    return (
        <div className="w-[50vw] md:w-[24vw] h-fit md:px-4 md:pt-1">
            <div className="order-none mb-4 flex-none flex-grow-0 text-base md:text-xl font-medium text-secondary">
                Subir imagen
            </div>
            <div className="h-[70px] md:h-[140px]">
                <div className="flex items-center gap-3 md:gap-6 h-full text-secondary">
                    <div className="md:ml-2 h-2/3 md:h-full aspect-square rounded-full outline outline-primary -outline-offset-1 outline-[2px]">
                        {image ? (
                            <div className={styles.canvas}>
                                <AvatarEditor
                                    image={image}
                                    ref={editorRef}
                                    disableBoundaryChecks
                                    borderRadius={0}
                                    height={2000}
                                    width={2000}
                                    border={5}
                                    rotate={0}
                                    scale={zoom / 10 + 0.3}
                                    className="w-full h-full z-1 rounded-full"
                                />
                            </div>
                        ) : (
                            <AvatarPlaceholder />
                        )}
                    </div>
                    <div className="flex flex-col gap-2 md:gap-4 w-full">
                        <CustomButton
                            className="flex h-fit w-fit text-xs md:text-base"
                            startIcon={window.innerWidth >= 768 && <ImageOutlined />}
                            text="Cargar imagen"
                            onClick={() => {
                                fileRef.current?.click()
                            }}
                            secondary={Boolean(image)}
                        />
                        <div className="font-medium text-secondary hidden md:block">
                            Podés arrastrar la imagen y acercarla para lograr un buen encuadre.
                        </div>
                        {image && (
                            <div className="w-[96%]">
                                <div className="font-medium text-secondary  ">Acercamiento</div>
                                <CustomSlider
                                    className="text-primary mx-1"
                                    marks={false}
                                    min={1}
                                    max={100}
                                    step={1}
                                    value={zoom}
                                    onChange={(event: Event, zoom: number | number[]) => {
                                        if (typeof zoom === 'number') {
                                            setZoom(zoom)
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {error ? <div className="mt-2 text-xs md:text-base">{error}</div> : null}
            <input
                ref={fileRef}
                accept="image/*"
                className="inset-x-0 inset-y-0 overflow-hidden whitespace-nowrap"
                style={{
                    clip: 'rect(0 0 0 0)',
                    clipPath: 'inset(50%)',
                    height: 1,
                    width: 1,
                }}
                type="file"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.files && e.target.files[0]) {
                        if (Math.round(e.target.files[0].size / 1024) > 1024) {
                            setError('El tamaño del archivo no puede exceder 1mb')

                            return
                        }
                        setError('')
                        setImage(e.target.files[0])
                    }
                }}
            />

            <div className="flex justify-end">
                <div className="w-full flex gap-4">
                    <CustomButton
                        className="bg-light-bg text-secondary w-full"
                        text="Cerrar"
                        onClick={handleClose}
                        secondary
                    />
                    <CustomButton
                        className="bg-secondary text-white w-full"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} /> : null}
                        text="Guardar"
                        onClick={saveHandler}
                    />
                </div>
            </div>
        </div>
    )
}

export default AvatarUploader
